import { apiClient } from './apiClient';
import { CategoryStatisticsResponse, CategoryTransactionsResponse, StatisticsFilters, AvailableDatesResponse } from './types';

export type ExportMode = 'MonthlyCategoryMatrix' | 'DetailedTransactions';
export type TransactionTypeFilter = 'Expenses' | 'Income' | 'Both';

export interface ExportParams {
  mode?: ExportMode;
  transactionType?: TransactionTypeFilter;
  includeSavings?: boolean;
  accountId?: string;
}

export const statisticsService = {
  async getCategoryStatistics(filters: StatisticsFilters): Promise<CategoryStatisticsResponse> {
    const params = new URLSearchParams();
    
    if (filters.dateFrom) params.append('dateFrom', filters.dateFrom);
    if (filters.dateTo) params.append('dateTo', filters.dateTo);
    if (filters.accountId) params.append('accountId', filters.accountId);
    
    return apiClient.get<CategoryStatisticsResponse>(
      `/statistics/categories?${params.toString()}`
    );
  },

  async getIncomeCategoryStatistics(filters: StatisticsFilters): Promise<CategoryStatisticsResponse> {
    const params = new URLSearchParams();
    
    if (filters.dateFrom) params.append('dateFrom', filters.dateFrom);
    if (filters.dateTo) params.append('dateTo', filters.dateTo);
    if (filters.accountId) params.append('accountId', filters.accountId);
    
    return apiClient.get<CategoryStatisticsResponse>(
      `/statistics/income/categories?${params.toString()}`
    );
  },

  async getCategoryTransactions(
    categoryId: string,
    filters: StatisticsFilters,
    transactionType?: 'expenses' | 'income'
  ): Promise<CategoryTransactionsResponse> {
    const params = new URLSearchParams();
    
    if (filters.dateFrom) params.append('dateFrom', filters.dateFrom);
    if (filters.dateTo) params.append('dateTo', filters.dateTo);
    if (filters.accountId) params.append('accountId', filters.accountId);
    if (transactionType) {
      params.append('type', transactionType);
    }
    
    return apiClient.get<CategoryTransactionsResponse>(
      `/statistics/categories/${categoryId}/transactions?${params.toString()}`
    );
  },

  async getAvailableDates(type?: 'expenses' | 'income', accountId?: string): Promise<AvailableDatesResponse> {
    const params = new URLSearchParams();
    
    if (type) params.append('type', type);
    if (accountId) params.append('accountId', accountId);
    
    return apiClient.get<AvailableDatesResponse>(
      `/statistics/available-dates?${params.toString()}`
    );
  },

  async exportToCsv(params: ExportParams = {}): Promise<void> {
    const searchParams = new URLSearchParams();
    if (params.mode) searchParams.set('mode', params.mode);
    if (params.transactionType) searchParams.set('transactionType', params.transactionType);
    if (params.includeSavings !== undefined) searchParams.set('includeSavings', String(params.includeSavings));
    if (params.accountId) searchParams.set('accountId', params.accountId);

    const { blob, fileName } = await apiClient.getBlob(`/statistics/export?${searchParams.toString()}`);
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName ?? `statistics_${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }
};

